import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';

async function get<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
  return await axios.get<T>(url, config);
}

async function post<T>(
  url: string,
  data: unknown,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
  return await axios.post<T>(url, data, config);
}

async function generic<T>(
  url: string,
  method: string | 'put' | 'post' | 'get',
  data: unknown
): Promise<AxiosResponse<T>> {
  return await axios<T>({ method, url, data });
}

async function put<T>(
  url: string,
  data: unknown,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
  return await axios.put<T>(url, data, config);
}

async function patch<T>(
  url: string,
  data: unknown,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
  return await axios.patch<T>(url, data, config);
}

async function _delete<T>(
  url: string,
  config?: AxiosRequestConfig
): Promise<AxiosResponse<T>> {
  return await axios.delete<T>(url, config);
}

function addCommonHeader(headerKey: string, headerValue: string): void {
  axios.defaults.headers.common[headerKey] = headerValue;
}

function removeCommonHeader(headerKey: string): void {
  // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
  delete axios.defaults.headers.common[headerKey];
}

function addRequestInterceptor(
  onFulfilled?: any,
  onRejected?: (error: unknown) => void
): number {
  return axios.interceptors.request.use(onFulfilled, onRejected);
}

function removeRequestInterceptor(id: number): void {
  axios.interceptors.request.eject(id);
}

function addResponseInterceptor(
  onFulfilled?: (
    config: AxiosResponse
  ) => AxiosResponse | Promise<AxiosResponse>,
  onRejected?: (error: unknown) => void
): number {
  return axios.interceptors.response.use(onFulfilled, onRejected);
}

function removeResponseInterceptor(id: number): void {
  axios.interceptors.response.eject(id);
}

function setBaseUrl(url: string): void {
  axios.defaults.baseURL = url;
}

function isError(error: unknown): error is AxiosError {
  return axios.isAxiosError(error);
}

async function cloneRequest<
  Response = unknown,
  HttpResponse = AxiosResponse<Response>
>(config: AxiosRequestConfig): Promise<HttpResponse> {
  return await axios.request(config) as unknown as HttpResponse;
}

const http = {
  get,
  post,
  put,
  patch,
  generic,
  delete: _delete,
  addCommonHeader,
  removeCommonHeader,
  addRequestInterceptor,
  removeRequestInterceptor,
  addResponseInterceptor,
  removeResponseInterceptor,
  setBaseUrl,
  isError,
  cloneRequest
};

export default http;
